import React from 'react';
import { ApolloProvider } from '@apollo/client';
import { injectStyle } from 'react-toastify/dist/inject-style';
import { Stack, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import 'react-toastify/dist/ReactToastify.css';
import { queryClient } from '@global-utils/queryClient';
import { persistableQueryKeys } from '@root/constants/queryKeys';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary, FallbackRender } from '@sentry/react';
import Routes from '@root/routes/Routes';
import { appTheme } from '@root/styles/theme';

import UnsupportedBrowser from '@global-pages/UnsupportedBrowser';

import AuthProvider from '@global-contexts/auth';
import GTMProvider from '@global-contexts/gtm';
import TagProvider from '@global-contexts/tag';
import LoadingProvider from '@global-contexts/loading';
import ToastProvider from '@global-contexts/toast';
import VersioningProvider from '@global-contexts/versioning';
import UserInteractionsProvider from '@global-contexts/userInteractions';

import { graphqlClient } from '@global-apis/config';

import logo from '@global-assets/images/logo/builderpad-logo-dark.svg';
import dotsLoading from '@global-assets/images/dots-loading.svg';
import '@global-assets/scss/app.scss';

import { browserTls13 } from '@global-utils/Helpers';
import { isStaleBuildError, reloadForNewBuild } from '@global-utils/deployRecovery';

import packageInfo from '../package.json';

const APP_VERSION = packageInfo.version;

// CALL IT ONCE IN THE APP
if (typeof window !== 'undefined') {
  injectStyle();
}

import { persister } from '@global-utils/persister';

const NewVersionAlert = () => {
  return (
    <Stack direction="column" justifyContent="center" alignItems="center" sx={{ height: '100vh' }} gap={6}>
      <img src={logo} alt="BuilderPad logo" width="200px" />

      <Typography variant="h5">New version is available!</Typography>

      <Typography variant="h6">{`You'll be redirected soon`}</Typography>

      <img src={dotsLoading} alt="loading" width="80px" />
    </Stack>
  );
};

const AppFallback: FallbackRender = ({ error, resetError }) => {
  // Vite reports a missing page file as a TypeError, never webpack's ChunkLoadError,
  // so this used to miss it; and it only ever reloaded once per browser session.
  const [isReloading] = React.useState(() => isStaleBuildError(error) && reloadForNewBuild('app'));

  React.useEffect(() => {
    if (!isStaleBuildError(error)) {
      resetError();
    }
  }, [error, resetError]);

  return isReloading ? <NewVersionAlert /> : <></>;
};

const App = () => {
  const [_, setTick] = React.useState(0);

  React.useEffect(() => {
    const handleStorage = () => setTick((t) => t + 1);
    window.addEventListener('user-localstorage-changed', handleStorage);
    return () => window.removeEventListener('user-localstorage-changed', handleStorage);
  }, []);

  if (!browserTls13()) {
    return <UnsupportedBrowser />;
  }

  const CACHED_COMPANY_ID = localStorage.getItem('__company_id') || '0';
  const CACHED_USER_ID = localStorage.getItem('__user_id') || '0';

  return (
    <ErrorBoundary fallback={AppFallback}>
      <HotkeysProvider
        initiallyActiveScopes={[
          'invoice-management-edit-table',
          'confirmation-modal',
          'invoice-detail-new-line-item-contract-cost-table',
          'invoice-detail-new-line-item-direct-cost-table',
          'invoice-detail-sidebar',
          'pagination-buttons',
          'simple-confirmation-modal',
          'topbar-search',
          'global'
        ]}>
        <ApolloProvider client={graphqlClient}>
          <LoadingProvider>
            <ToastProvider>
              <PersistQueryClientProvider
                client={queryClient}
                persistOptions={{
                  persister,
                  buster: `${APP_VERSION}.${CACHED_COMPANY_ID}.${CACHED_USER_ID}`,
                  dehydrateOptions: {
                    shouldDehydrateQuery: (query) =>
                      query.state.status === 'success' && persistableQueryKeys.some((key) => query.queryKey[0] === key)
                  }
                }}>
                <AuthProvider>
                  <VersioningProvider>
                    <TagProvider>
                      <GTMProvider>
                        <ThemeProvider theme={appTheme}>
                          <UserInteractionsProvider>
                            <CssBaseline />
                            <Routes />
                          </UserInteractionsProvider>
                        </ThemeProvider>
                      </GTMProvider>
                    </TagProvider>
                  </VersioningProvider>
                </AuthProvider>
              </PersistQueryClientProvider>
              {/* <ReactQueryDevtools client={queryClient} /> */}
            </ToastProvider>
          </LoadingProvider>
        </ApolloProvider>
      </HotkeysProvider>
    </ErrorBoundary>
  );
};

export default App;
